import { useEffect, useMemo, useState } from 'react';
import {
  type DoraLevel,
  type IPortfolioDoraMetric,
  type IPortfolioDoraTrendBucket,
  type IPortfolioDoraTrendResponse,
} from '@skystone/shared';
import { Spinner, ErrorBanner } from '@/components/ui';
import { portfolioApi } from '@/services/portfolio-api';
import { formatLeadTime, formatCFR, formatMTTR, roundLeadTimeForDisplay } from '@/lib/dora-utils';
import { formatPillSubLabel } from '@/lib/metric-view/date-presets';
import { buildBucketAxisProps, todayIsoUtc } from '@/lib/metric-view/bucket-format';
import type { IMetricViewFilters } from '@/hooks/use-metric-view-filters';
import { DoraMetricCard, type IDoraSparkPoint, type IDoraDelta } from './DoraMetricCard';
import { NordicReviewSection } from './NordicReviewSection';

interface EngineeringDoraTabProps {
  filters: IMetricViewFilters;
  /**
   * Scope product name. The review-flow section is step-1 scoped to Nordic ERP,
   * so it renders only when this matches — the endpoint itself is
   * repository-scoped and would happily return nordic-portal data under any
   * product, which would be wrong on every other product's page.
   */
  productName?: string | null;
  /** Resolved team name; null when "All Teams" is selected. */
  selectedTeamName?: string | null;
}

const NORDIC_REVIEW_PRODUCT_NAME = 'Nordic ERP';

type MetricKey = 'deployFrequency' | 'leadTimeDays' | 'cfrPercent' | 'mttrHours';

/**
 * Compute a delta between current and prior aggregates. The label format
 * mirrors the headline's value unit so the eye doesn't have to reconcile
 * different units (e.g. headline "<0.1h" with delta "→ 0d" — confusing).
 *
 * `formatAbs` formats a positive magnitude with the metric's natural unit.
 * `flatThreshold` is the absolute diff below which we render "flat / 0".
 */
function computeDelta(
  current: number | null,
  prior: number | null,
  formatAbs: (absMagnitude: number) => string,
  flatThreshold: number,
): IDoraDelta | null {
  if (current === null || prior === null) return null;
  const diff = current - prior;
  const abs = Math.abs(diff);
  if (abs < flatThreshold) {
    return { direction: 'flat', label: '0' };
  }
  const sign = diff > 0 ? '+' : '-';
  return {
    direction: diff > 0 ? 'up' : 'down',
    label: `${sign}${formatAbs(abs)}`,
  };
}

/**
 * MTTR delta uses the same h/d crossover as the headline, but unlike
 * `formatMTTR` it doesn't collapse small-but-nonzero diffs to "< 1h".
 */
function formatHoursAbs(h: number): string {
  if (h < 1) return `${(h * 60).toFixed(0)}min`;
  if (h < 24) return `${h.toFixed(1)}h`;
  return `${(h / 24).toFixed(1)}d`;
}

function bucketsToSeries(
  buckets: IPortfolioDoraTrendBucket[],
  metric: MetricKey,
): IDoraSparkPoint[] {
  return buckets.map((b) => ({ bucketStart: b.bucketStart, value: b[metric] }));
}

export function EngineeringDoraTab({
  filters,
  productName,
  selectedTeamName,
}: Readonly<EngineeringDoraTabProps>) {
  const [data, setData] = useState<IPortfolioDoraTrendResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    portfolioApi
      .getDoraTrend({
        from: filters.from,
        to: filters.to,
        productId: filters.productId || undefined,
        teamId: filters.teamId || undefined,
      })
      .then((response) => {
        if (cancelled) return;
        setData(response);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError('Failed to load DORA metrics. Please try again.');
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [filters.from, filters.to, filters.productId, filters.teamId]);

  // Hook must run on every render — keep before the loading early return.
  const axis = useMemo(
    () => buildBucketAxisProps({
      today: todayIsoUtc(),
      rangeFrom: filters.from,
      rangeTo: filters.to,
    }),
    [filters.from, filters.to],
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner />
      </div>
    );
  }

  // Empty-state fallback (epic D12): use null-valued metrics so each card
  // renders its "—" big number, no badge, no delta — but the card structure
  // and sparkline boundaries stay visible.
  const emptyMetric: IPortfolioDoraMetric = {
    deployFrequency: null, deployFrequencyLevel: null,
    leadTimeDays: null, leadTimeLevel: null,
    cfrPercent: null, cfrLevel: null,
    mttrHours: null, mttrLevel: null,
  };
  const aggregate = data?.aggregate ?? emptyMetric;
  const priorAggregate = data?.priorAggregate ?? emptyMetric;
  const buckets = data?.buckets ?? [];

  const subLabel = formatPillSubLabel(filters.pill);

  return (
    <div className="space-y-3 p-2">
      {error && <ErrorBanner message={error} />}

      <p
        className="text-[11px] italic text-muted-foreground"
        data-testid="dora-product-scope-caveat"
      >
        DORA metrics are computed at the product level — team selection narrows to the
        team's product context, not to the team itself.
      </p>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <DoraMetricCard
          title="Deployment Frequency"
          definition="Deploys to production per week — DORA tier shown."
          metricKey="deployFrequency"
          unit="/wk"
          formatValue={(n) => n.toFixed(1)}
          series={bucketsToSeries(buckets, 'deployFrequency')}
          latestValue={aggregate.deployFrequency}
          level={aggregate.deployFrequencyLevel as DoraLevel | null}
          delta={computeDelta(
            aggregate.deployFrequency,
            priorAggregate.deployFrequency,
            (n) => `${n.toFixed(1)}/wk`,
            0.05,
          )}
          eliteHint="Elite: ≥ 7/wk (on-demand)"
          latestLabel={subLabel}
          axis={axis}
        />

        <DoraMetricCard
          title="PR Lead Time (median)"
          definition="Pull Request Lead Time (Created → Merged)"
          metricKey="leadTime"
          unit=""
          formatValue={(d) => formatLeadTime(d, true)}
          series={bucketsToSeries(buckets, 'leadTimeDays')}
          latestValue={aggregate.leadTimeDays}
          level={aggregate.leadTimeLevel as DoraLevel | null}
          delta={computeDelta(
            aggregate.leadTimeDays,
            priorAggregate.leadTimeDays,
            (d) => roundLeadTimeForDisplay(d),
            // 15 minutes — diffs smaller than this read as "flat" rather than noise.
            1 / 24 / 4,
          )}
          eliteHint="Elite: < 1 hr"
          latestLabel={subLabel}
          axis={axis}
        />

        <DoraMetricCard
          title="Change Failure Rate"
          definition="% of deploys causing an incident, rollback, or hotfix."
          metricKey="cfr"
          unit=""
          formatValue={(p) => formatCFR(p)}
          series={bucketsToSeries(buckets, 'cfrPercent')}
          latestValue={aggregate.cfrPercent}
          level={aggregate.cfrLevel as DoraLevel | null}
          delta={computeDelta(
            aggregate.cfrPercent,
            priorAggregate.cfrPercent,
            (p) => `${p.toFixed(1)}pp`,
            0.05,
          )}
          eliteHint="Elite: 0–5%"
          latestLabel={subLabel}
          axis={axis}
        />

        <DoraMetricCard
          title="Mean Time to Restore"
          definition="Average time to restore service after a production incident."
          metricKey="mttr"
          unit=""
          formatValue={(h) => formatMTTR(h)}
          series={bucketsToSeries(buckets, 'mttrHours')}
          latestValue={aggregate.mttrHours}
          level={aggregate.mttrLevel as DoraLevel | null}
          delta={computeDelta(
            aggregate.mttrHours,
            priorAggregate.mttrHours,
            formatHoursAbs,
            // 6 minutes — typical MTTR resolution sits in tens of minutes minimum.
            0.1,
          )}
          eliteHint="Elite: < 1 hr"
          latestLabel={subLabel}
          emptyMessage="No production incidents in this period — elite continues."
          axis={axis}
        />
      </div>

      {productName === NORDIC_REVIEW_PRODUCT_NAME && (
        <NordicReviewSection filters={filters} selectedTeamName={selectedTeamName ?? null} />
      )}
    </div>
  );
}
